import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { snackbar, FullScreenProgress } from '@occc-app/design-system';

import { RetailerNetworkLocationGroupDetailsPage } from './retailer-network-location-group-details-page.component';

import { RetailerNetworkNoData } from '../components/retailer-network-no-data.component';
import {
  useGetRetailerNetworkLocationGroups,
  useGetRetailerNetworkQuery,
} from '../../data-access/queries';
import {
  useRetailerNetworkLocationGroupAddLocationsMutation,
  useRetailerNetworkLocationGroupRemoveLocationsMutation,
} from '../../data-access/mutations';
import { useBreadcrumbLinks } from '../hooks/use-breadcrumb-links.hook';
import { routeCreator } from '../retailer-connect.utils';
import { RetailerNetworkErrorMessage } from '../components/retailer-network-error-message.component';

export const RetailerNetworkLocationGroupDetailsPageContainer = () => {
  const { networkId = '', locationGroupId = '' } = useParams<{
    networkId: string;
    locationGroupId: string;
  }>();
  const {
    data: retailerNetworkData,
    loading: isRetailerNetworkDataLoading,
    error: retailerNetworkDataError,
  } = useGetRetailerNetworkQuery({
    variables: { uuid: networkId },
  });
  const {
    data: retailerNetworkLocationGroupData,
    loading: isRetailerNetworkLocationGroupDataLoading,
    error: retailerNetworkLocationGroupDataError,
  } = useGetRetailerNetworkLocationGroups({
    filter: {
      retailerNetworkUuidEquals: networkId,
      retailerNetworkLocationGroupUuidEquals: locationGroupId,
    },
    pageSize: 1,
  });

  const [
    retailerNetworkLocationGroupAddLocations,
    { loading: isAddLocationToLocationGroupInProgress },
  ] = useRetailerNetworkLocationGroupAddLocationsMutation({
    onCompleted: () => snackbar.success('Location(s) successfully added to the Location Group'),
    onError: () => snackbar.error('Error adding Location(s) to the Location Group'),
    refetchQueries: [
      'retailerNetworkLocationsWithPerformanceExcerpt',
      'retailerNetworkPerformance',
      'retailerNetworkLocationGroups',
    ],
    awaitRefetchQueries: true,
  });

  const addLocationsToLocationGroup = async (locationUuids: string[]) => {
    await retailerNetworkLocationGroupAddLocations({
      variables: {
        retailerNetworkUuid: networkId,
        locationGroupUuid: locationGroupId,
        locationUuids: locationUuids,
      },
    });
  };

  const [
    retailerNetworkLocationGroupRemoveLocation,
    { loading: isRemoveLocationFromGroupInProgress },
  ] = useRetailerNetworkLocationGroupRemoveLocationsMutation({
    onCompleted: () => snackbar.success('Location(s) successfully removed from the Location Group'),
    onError: () => snackbar.error('Error removing Location(s) from the Location Group'),
    refetchQueries: [
      'retailerNetworkLocationsWithPerformanceExcerpt',
      'retailerNetworkPerformance',
      'retailerNetworkLocationGroups',
    ],
    awaitRefetchQueries: true,
  });

  const removeLocationFromLocationGroup = async (locationUuid: string) => {
    await retailerNetworkLocationGroupRemoveLocation({
      variables: {
        retailerNetworkUuid: networkId,
        locationGroupUuid: locationGroupId,
        locationUuids: [locationUuid],
      },
    });
  };

  const locationGroup = retailerNetworkLocationGroupData?.[0];

  const breadcrumbLinks = useMemo(
    () => [
      { label: 'My Stores', to: routeCreator.myStores(networkId, true) },
      { label: locationGroup?.name || 'Group Details' },
    ],
    [locationGroup?.name, networkId],
  );

  useBreadcrumbLinks(breadcrumbLinks);

  const data = retailerNetworkData && retailerNetworkLocationGroupData && locationGroup;
  const loading = isRetailerNetworkDataLoading || isRetailerNetworkLocationGroupDataLoading;
  const error = retailerNetworkDataError || retailerNetworkLocationGroupDataError;

  if (loading && !data) {
    return <FullScreenProgress />;
  }

  if (error) {
    return <RetailerNetworkErrorMessage />;
  }

  if (!data) {
    return <RetailerNetworkNoData />;
  }

  return (
    <RetailerNetworkLocationGroupDetailsPage
      locationGroup={locationGroup}
      retailerNetwork={retailerNetworkData.retailerNetwork}
      addLocationsToLocationGroup={addLocationsToLocationGroup}
      removeLocationFromLocationGroup={removeLocationFromLocationGroup}
      isRemoveLocationFromGroupInProgress={isRemoveLocationFromGroupInProgress}
      isAddLocationToLocationGroupInProgress={isAddLocationToLocationGroupInProgress}
    />
  );
};
